import { ProductionPlanningStep, AutoScheduleStepResult } from 'models/production-planning';
import { Order, SampleType } from 'models/order';
import { LabAction } from 'models/action';
import { ProductionPlanningTemplate } from 'models/organization';
import { formatDate } from 'utils/format';

// ============================================================================
// Work Units
// ============================================================================

export type ConstructionForTemplateMatching = {
    actions: { actionId: string; quantity: string }[];
};

export const calculateWorkUnits = (constructions?: ConstructionForTemplateMatching[]): number => {
    if (!constructions?.length) return 1;
    return constructions.reduce((total, construction) => total + (construction.actions?.length || 0), 0) || 1;
};

// ============================================================================
// Template Matching
// ============================================================================

export const getTemplateIdForSampleType = (
    productionTemplate: { digitalTemplateId?: string | null; physicalTemplateId?: string | null },
    sampleType?: string,
): string | null | undefined => {
    if (sampleType === SampleType.DIGITAL) return productionTemplate.digitalTemplateId;
    if (sampleType === SampleType.PHYSICAL) return productionTemplate.physicalTemplateId;
    return productionTemplate.digitalTemplateId || productionTemplate.physicalTemplateId;
};

export type ActionBasedAction = {
    _id: string;
    productionTemplate?: {
        planningDigitalTemplateId?: { _id: string } | null;
        planningPhysicalTemplateId?: { _id: string } | null;
    } | null;
};

export const getActionBasedTemplateId = (action: ActionBasedAction, sampleType?: string): string | null => {
    const template = action.productionTemplate;
    if (!template) return null;
    if (sampleType === SampleType.DIGITAL) return template.planningDigitalTemplateId?._id || null;
    if (sampleType === SampleType.PHYSICAL) return template.planningPhysicalTemplateId?._id || null;
    return template.planningDigitalTemplateId?._id || template.planningPhysicalTemplateId?._id || null;
};

export const collectMatchingTemplateIds = (
    orderActionIds: Set<string>,
    labActions: LabAction[],
    actionBasedActions: ActionBasedAction[],
    sampleType?: string,
): Set<string> => {
    const templateIds = new Set<string>();

    labActions.forEach((action) => {
        if (action._id && orderActionIds.has(action._id) && action.productionPlanningTemplate) {
            const templateId = getTemplateIdForSampleType(action.productionPlanningTemplate, sampleType);
            if (templateId) templateIds.add(templateId);
        }
    });

    actionBasedActions.forEach((action) => {
        if (action._id && orderActionIds.has(action._id)) {
            const templateId = getActionBasedTemplateId(action, sampleType);
            if (templateId) templateIds.add(templateId);
        }
    });

    return templateIds;
};

export const buildTemplateQuantityMap = (
    constructions: ConstructionForTemplateMatching[],
    labActions: LabAction[],
    actionBasedActions: ActionBasedAction[],
    sampleType?: string,
): Map<string, number> => {
    const actionQuantities = new Map<string, number>();
    constructions.forEach((construction) => {
        construction.actions.forEach((action) => {
            const quantity = parseFloat(action.quantity) || 1;
            const existing = actionQuantities.get(action.actionId) || 0;
            actionQuantities.set(action.actionId, existing + quantity);
        });
    });

    const templateQuantityMap = new Map<string, number>();

    const resolveQuantity = (actionId: string, templateId: string) => {
        const actionQty = actionQuantities.get(actionId) || 1;
        const existingQty = templateQuantityMap.get(templateId) || 0;
        templateQuantityMap.set(templateId, Math.max(existingQty, actionQty));
    };

    labActions.forEach((action) => {
        if (action._id && actionQuantities.has(action._id) && action.productionPlanningTemplate) {
            const templateId = getTemplateIdForSampleType(action.productionPlanningTemplate, sampleType);
            if (templateId) resolveQuantity(action._id, templateId);
        }
    });

    actionBasedActions.forEach((action) => {
        if (action._id && actionQuantities.has(action._id)) {
            const templateId = getActionBasedTemplateId(action, sampleType);
            if (templateId) resolveQuantity(action._id, templateId);
        }
    });

    return templateQuantityMap;
};

export const buildStepsFromTemplate = (
    template: ProductionPlanningTemplate,
    quantity: number,
    constructions: ConstructionForTemplateMatching[] | undefined,
    getDepartmentIdForStep: (stepId: string) => string | undefined,
    existingStepIds: Set<string | undefined>,
    alreadyAdded: Set<string | undefined>,
): ProductionPlanningStep[] => {
    const newSteps: ProductionPlanningStep[] = [];
    template.workflowSteps.forEach((ws) => {
        const workflowStepId = ws.stepId;
        if (!workflowStepId) return;
        if (!existingStepIds.has(workflowStepId) && !alreadyAdded.has(workflowStepId)) {
            const adjustedProductionHours = calculateProductionHours(
                ws.estimatedProductionHours ?? 0,
                ws.affectedByQuantityModifier ?? 0,
                quantity,
            );
            newSteps.push({
                stepId: workflowStepId,
                stepName: ws.name,
                technicianId: '',
                technicianName: '',
                startDate: null,
                deadline: null,
                estimatedProductionHours: adjustedProductionHours,
                estimatedMachineHours: ws.machineId ? undefined : ws.estimatedMachineHours || undefined,
                machineId: ws.machineId || undefined,
                stepOrder: ws.stepOrder ?? undefined,
                allowOvernight: ws.allowOvernight || false,
                startNextDay: ws.startNextDay || false,
                affectedByQuantityModifier: ws.affectedByQuantityModifier ?? 0,
                departmentId: getDepartmentIdForStep(workflowStepId),
                workUnits: calculateWorkUnits(constructions),
            });
            alreadyAdded.add(workflowStepId);
        }
    });
    return newSteps;
};

export const calculateProductionHours = (
    estimatedProductionHours: number,
    modifier: number,
    quantity: number,
): number | undefined => {
    if (modifier > 0 && quantity > 1) {
        return estimatedProductionHours + (quantity - 1) * (estimatedProductionHours * modifier);
    }
    return estimatedProductionHours || undefined;
};

// ============================================================================
// Order ↔ Step Mapping
// ============================================================================

export const mapOrderPlanToSteps = (order: Order): ProductionPlanningStep[] =>
    (order.productionPlan || []).map((s) => ({
        stepId: s.stepId,
        stepName: s.stepName,
        technicianId: s.technicianId || '',
        technicianName: s.technicianName || '',
        startDate: s.startDate ? new Date(s.startDate) : null,
        deadline: s.deadline ? new Date(s.deadline) : null,
        estimatedProductionHours: s.estimatedProductionHours ?? 0,
        estimatedMachineHours: s.estimatedMachineHours ?? 0,
        stepOrder: s.stepOrder,
        allowOvernight: s.allowOvernight ?? false,
        startNextDay: s.startNextDay ?? false,
        machineId: s.machineId || undefined,
        departmentId: s.departmentId || undefined,
        affectedByQuantityModifier: s.affectedByQuantityModifier,
        workUnits: s.workUnits || 0,
    }));

export const stepsToProductionPlanInput = (steps: ProductionPlanningStep[]) =>
    steps.map((s, i) => ({
        stepId: s.stepId || '',
        stepName: s.stepName,
        technicianId: s.technicianId || undefined,
        technicianName: s.technicianName || undefined,
        startDate: s.startDate,
        deadline: s.deadline,
        estimatedProductionHours: s.estimatedProductionHours,
        estimatedMachineHours: s.estimatedMachineHours,
        stepOrder: i,
        allowOvernight: s.allowOvernight ?? false,
        startNextDay: s.startNextDay ?? false,
        affectedByQuantityModifier: s.affectedByQuantityModifier,
        workUnits: s.workUnits || 0,
        departmentId: s.departmentId || undefined,
        machineId: s.machineId || undefined,
    }));

export const formatStepDateTime = (date: Date | null | undefined): string => {
    if (!date) return '-';
    return formatDate(date, 'datetime');
};

// ============================================================================
// Date Helpers
// ============================================================================

export const countWeekdaysFromStart = (from: Date, to: Date): number => {
    const start = new Date(from);
    start.setHours(0, 0, 0, 0);
    const end = new Date(to);
    end.setHours(0, 0, 0, 0);
    if (end <= start) return 1;
    let count = 1;
    const current = new Date(start);
    while (current < end) {
        current.setDate(current.getDate() + 1);
        const day = current.getDay();
        if (day !== 0 && day !== 6) count++;
    }
    return count;
};

// ============================================================================
// Schedule Results
// ============================================================================

export const mergeScheduleResults = (
    steps: ProductionPlanningStep[],
    results: AutoScheduleStepResult[],
): ProductionPlanningStep[] => {
    return steps.map((step) => {
        const result = results.find((r) => r.stepId === step.stepId);
        return result
            ? {
                  ...step,
                  startDate: new Date(result.startDate),
                  deadline: new Date(result.deadline),
                  dayNumber: result.dayNumber,
                  ...(result.estimatedProductionHours != null && {
                      estimatedProductionHours: result.estimatedProductionHours,
                  }),
              }
            : step;
    });
};

// ============================================================================
// Validation
// ============================================================================

export const validateStepsChronological = (steps: ProductionPlanningStep[]): boolean => {
    for (let i = 1; i < steps.length; i++) {
        const prevDeadline = steps[i - 1].deadline;
        const currStart = steps[i].startDate;

        if (prevDeadline && currStart && new Date(prevDeadline).getTime() > new Date(currStart).getTime()) {
            return false;
        }

        const currDeadline = steps[i].deadline;
        if (currStart && currDeadline && new Date(currStart).getTime() > new Date(currDeadline).getTime()) {
            return false;
        }
    }
    return true;
};

export const validateDeadline = (deadline: Date | null, internalDeliveryDate: Date | null | undefined): boolean => {
    if (!deadline) return true;
    const deadlineTime = new Date(deadline).getTime();
    const now = new Date().getTime();
    if (deadlineTime < now) return false;
    if (internalDeliveryDate) {
        const prodDeadline = new Date(internalDeliveryDate);
        prodDeadline.setHours(23, 59, 59, 999);
        if (deadlineTime > prodDeadline.getTime()) return false;
    }
    return true;
};

export const validateStartDate = (step: ProductionPlanningStep, index: number): boolean => {
    if (!step.startDate) return true;
    const startTime = new Date(step.startDate).getTime();
    const now = new Date().getTime();
    if (index === 0 && startTime < now) return false;
    if (step.deadline && startTime > new Date(step.deadline).getTime()) return false;
    return true;
};
